import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, DUCK_LOGO } from "@/lib/api";
import { X, Download, Copy, Instagram, Twitter, Facebook, Share2, Loader2 } from "lucide-react";

/**
 * ShareVisionBoard — renders the user's goal + AI hero image onto a
 * social-media-ready canvas (1080x1350 IG portrait), applies the Credit
 * Quack gold watermark, and hands off via Web Share API / download / X intent.
 */
export default function ShareVisionBoard({ goal, onClose }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(true);

  const theme = goal?.theme;
  const shareCaption = `${theme?.emoji || "⭐"} My goal: ${goal?.goal_label || "Better credit"}\n\nBuilt with @CreditQuack — the smartest way to track credit repair + funding.\n#CreditQuack #Goals #Funding`;

  useEffect(() => {
    if (!goal) return;
    let cancelled = false;
    (async () => {
      const url = await composeVisionBoard(goal);
      if (cancelled) return;
      setDataUrl(url);
      setBusy(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.goal_text, goal?.theme?.id, goal?.hero_image_data_url]);

  const track = (channel) => {
    api.post("/client/goal/track-share", { channel }).catch(() => {});
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `credit-quack-vision-${(goal?.goal_label || "goal").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    a.click();
    track("download");
    toast.success("Downloaded — post it anywhere ✨");
  };

  const shareNative = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "vision-board.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Credit Quack Vision", text: shareCaption });
        track("native");
        return;
      }
    } catch (e) { /* user cancelled or unsupported */ }
    // Fallback: copy caption + open download
    try {
      await navigator.clipboard.writeText(shareCaption);
      toast.success("Caption copied — save the image and paste!");
    } catch {}
    download();
  };

  const openTwitter = () => {
    track("twitter");
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareCaption)}`, "_blank");
    toast.info("Tweet composer open — attach your saved image!");
    download();
  };

  const openFacebook = () => {
    track("facebook");
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`, "_blank");
    download();
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(shareCaption);
      track("copy");
      toast.success("Caption copied to clipboard.");
    } catch { toast.error("Couldn't copy — long-press the caption instead."); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <Card
        className="max-w-3xl w-full rounded-3xl overflow-hidden my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-vision-modal"
      >
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-300">Vision Board</p>
              <h3 className="font-display text-2xl sm:text-3xl font-black leading-tight mt-1">
                Post your goal. Rally your people.
              </h3>
              <p className="text-slate-300 text-sm mt-0.5">Watermarked with Credit Quack — every share plants a seed.</p>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-white p-1" data-testid="share-vision-close"><X size={20} /></button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Preview */}
          <div className="bg-slate-100 p-4 flex items-center justify-center min-h-[400px]">
            {busy && <Loader2 className="animate-spin text-slate-400" size={32} />}
            {dataUrl && (
              <img
                src={dataUrl}
                alt="Vision board preview"
                className="max-w-full max-h-[520px] rounded-xl shadow-2xl"
                data-testid="share-vision-preview"
              />
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          {/* Actions */}
          <div className="p-6 space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-slate-500">Share your vision</p>

            <Button onClick={shareNative} disabled={busy} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg" data-testid="share-vision-native">
              <Share2 size={14} className="mr-2" /> Share anywhere…
            </Button>

            <div className="grid grid-cols-2 gap-2">
              <Button onClick={download} disabled={busy} variant="outline" className="border-slate-200" data-testid="share-vision-download">
                <Download size={13} className="mr-1" /> Download
              </Button>
              <Button onClick={copyCaption} variant="outline" className="border-slate-200" data-testid="share-vision-copy">
                <Copy size={13} className="mr-1" /> Copy caption
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-2">
              <button onClick={() => { track("instagram"); toast.info("IG: save the image, then paste caption on your post!"); copyCaption(); download(); }}
                      className="rounded-xl border border-slate-200 hover:border-pink-400 py-2.5 flex flex-col items-center gap-1 transition"
                      data-testid="share-vision-instagram">
                <Instagram size={16} className="text-pink-500" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">Instagram</span>
              </button>
              <button onClick={openTwitter}
                      className="rounded-xl border border-slate-200 hover:border-slate-900 py-2.5 flex flex-col items-center gap-1 transition"
                      data-testid="share-vision-twitter">
                <Twitter size={16} className="text-slate-900" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">X / Twitter</span>
              </button>
              <button onClick={openFacebook}
                      className="rounded-xl border border-slate-200 hover:border-blue-500 py-2.5 flex flex-col items-center gap-1 transition"
                      data-testid="share-vision-facebook">
                <Facebook size={16} className="text-blue-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">Facebook</span>
              </button>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">Caption preview</p>
              <p className="text-xs text-slate-700 whitespace-pre-line bg-slate-50 rounded-lg p-3 border border-slate-100">{shareCaption}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>,
    document.body
  );
}

// --------------------------------------------------------------------
// Canvas composer — draws the hero image + branded lower panel with the
// goal label, quote, and CQ watermark. Layout is measured top-down so
// nothing overlaps regardless of how long the label / quote runs.
// Returns a PNG data URL sized for Instagram portrait (1080x1350).
// --------------------------------------------------------------------
async function composeVisionBoard(goal) {
  const W = 1080, H = 1350;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  const theme = goal?.theme || {};
  const primary = theme.primary || "#F59E0B";
  const accent = theme.accent || "#F59E0B";

  const PAD_X = 72;
  const MAX_TEXT_W = W - PAD_X * 2;
  const WATERMARK_H = 72;

  // ---- Measure text blocks first (top-down) so we can size the panel exactly.
  const label = (goal?.goal_label || "Better credit. Real funding.").slice(0, 70);
  const quote = (goal?.goal_text || "").slice(0, 180);
  const emoji = theme.emoji || "⭐";

  // Choose label font size that keeps it to <= 3 lines
  const labelFontMax = 72;
  const labelFontMin = 44;
  const labelLineHeightRatio = 1.12;
  let labelFontSize = labelFontMax;
  let labelLines = [];
  for (; labelFontSize >= labelFontMin; labelFontSize -= 4) {
    ctx.font = `900 ${labelFontSize}px 'Cabinet Grotesk',system-ui,-apple-system,'Segoe UI',sans-serif`;
    labelLines = wrapLines(ctx, label, MAX_TEXT_W);
    if (labelLines.length <= 3) break;
  }
  const labelLH = Math.round(labelFontSize * labelLineHeightRatio);
  const labelBlockH = labelLines.length * labelLH;

  // Quote (optional) — fixed font, wrap up to 3 lines then ellipsize
  let quoteLines = [];
  let quoteBlockH = 0;
  const quoteFontSize = 26;
  const quoteLH = 34;
  if (quote) {
    ctx.font = `500 ${quoteFontSize}px system-ui,-apple-system,'Segoe UI',sans-serif`;
    quoteLines = wrapLines(ctx, `"${quote}"`, MAX_TEXT_W);
    if (quoteLines.length > 3) {
      quoteLines = quoteLines.slice(0, 3);
      // Ellipsize last line
      const last = quoteLines[2];
      let truncated = last;
      while (ctx.measureText(truncated + "…\"").width > MAX_TEXT_W && truncated.length > 4) {
        truncated = truncated.slice(0, -1);
      }
      quoteLines[2] = truncated.replace(/[",\s]+$/, "") + "…\"";
    }
    quoteBlockH = quoteLines.length * quoteLH;
  }

  // Vertical rhythm inside the bottom panel
  const eyebrowH = 32;               // "MY GOAL · CREDIT QUACK"
  const emojiRowH = 84;              // emoji glyph
  const gapAfterEmojiRow = 22;
  const gapBeforeQuote = quote ? 26 : 0;
  const panelPadTop = 60;
  const panelPadBottom = 60;
  const panelInnerH =
    panelPadTop + emojiRowH + gapAfterEmojiRow + labelBlockH +
    gapBeforeQuote + quoteBlockH + panelPadBottom + WATERMARK_H;
  const panelH = Math.max(420, Math.min(680, panelInnerH));
  const heroH = H - panelH;

  // ---- Background gradient fallback
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#0F172A");
  bg.addColorStop(1, primary);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // ---- Hero image (cover-fit into the top area)
  let logoImg = null;
  try { logoImg = await loadImg(DUCK_LOGO); } catch { /* logo optional */ }

  if (goal?.hero_image_data_url) {
    try {
      const img = await loadImg(goal.hero_image_data_url);
      const ratio = Math.max(W / img.width, heroH / img.height);
      const drawW = img.width * ratio;
      const drawH = img.height * ratio;
      const dx = (W - drawW) / 2;
      const dy = (heroH - drawH) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, heroH);
      ctx.clip();
      ctx.drawImage(img, dx, dy, drawW, drawH);
      ctx.restore();
    } catch { /* fall through */ }
  }

  // ---- Brand badge (top-right corner over the hero)
  if (logoImg) {
    const badgeW = 260;
    const badgeH = 96;
    const badgeX = W - badgeW - 32;
    const badgeY = 32;
    ctx.save();
    // Dark pill backdrop
    roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 48);
    ctx.fillStyle = "rgba(15,23,42,0.72)";
    ctx.fill();
    // Gold hairline
    ctx.strokeStyle = primary;
    ctx.lineWidth = 2;
    ctx.stroke();
    // Logo
    const logoSize = 72;
    ctx.drawImage(logoImg, badgeX + 12, badgeY + (badgeH - logoSize) / 2, logoSize, logoSize);
    // Brand text
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.font = "900 22px system-ui,-apple-system,'Segoe UI',sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText("CREDIT QUACK", badgeX + 12 + logoSize + 8, badgeY + badgeH / 2 - 10);
    ctx.font = "700 12px system-ui,-apple-system,'Segoe UI',sans-serif";
    ctx.fillStyle = primary;
    ctx.fillText("VISION BOARD", badgeX + 12 + logoSize + 8, badgeY + badgeH / 2 + 12);
    ctx.restore();
    ctx.textAlign = "left";
  }

  // Soft fade at the bottom of hero into panel color
  const fade = ctx.createLinearGradient(0, heroH - 100, 0, heroH);
  fade.addColorStop(0, "rgba(15,23,42,0)");
  fade.addColorStop(1, "#0F172A");
  ctx.fillStyle = fade;
  ctx.fillRect(0, heroH - 100, W, 100);

  // ---- Bottom panel background (solid dark)
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, heroH, W, panelH);
  // Accent hairline
  ctx.fillStyle = primary;
  ctx.fillRect(0, heroH, W, 4);

  // ---- Text stack inside panel
  let cursorY = heroH + panelPadTop;

  // Row 1: emoji + eyebrow side by side
  ctx.textBaseline = "middle";
  ctx.font = "600 60px 'Apple Color Emoji','Segoe UI Emoji',system-ui,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.fillText(emoji, PAD_X, cursorY + emojiRowH / 2);

  ctx.font = "800 22px system-ui,-apple-system,'Segoe UI',sans-serif";
  ctx.fillStyle = accent;
  ctx.fillText("MY GOAL · CREDIT QUACK", PAD_X + 84, cursorY + emojiRowH / 2);

  cursorY += emojiRowH + gapAfterEmojiRow;

  // Row 2: label lines
  ctx.textBaseline = "top";
  ctx.font = `900 ${labelFontSize}px 'Cabinet Grotesk',system-ui,-apple-system,'Segoe UI',sans-serif`;
  ctx.fillStyle = "#fff";
  labelLines.forEach((line, i) => {
    ctx.fillText(line, PAD_X, cursorY + i * labelLH);
  });
  cursorY += labelBlockH;

  // Row 3: quote
  if (quoteLines.length) {
    cursorY += gapBeforeQuote;
    ctx.font = `500 ${quoteFontSize}px system-ui,-apple-system,'Segoe UI',sans-serif`;
    ctx.fillStyle = "#CBD5E1";
    quoteLines.forEach((line, i) => {
      ctx.fillText(line, PAD_X, cursorY + i * quoteLH);
    });
  }

  // Watermark bar (always pinned to bottom)
  ctx.fillStyle = primary;
  ctx.fillRect(0, H - WATERMARK_H, W, WATERMARK_H);
  ctx.textBaseline = "middle";
  // Logo tucked in the left side of the watermark bar
  if (logoImg) {
    const logoSz = 44;
    ctx.drawImage(logoImg, PAD_X, H - WATERMARK_H / 2 - logoSz / 2, logoSz, logoSz);
  }
  ctx.font = "800 22px system-ui,-apple-system,'Segoe UI',sans-serif";
  ctx.fillStyle = "#0F172A";
  ctx.fillText("CREDITQUACK.COM", PAD_X + (logoImg ? 56 : 0), H - WATERMARK_H / 2);
  ctx.textAlign = "right";
  ctx.fillText("YOUR CREDIT + FUNDING, ON A TRACKER", W - PAD_X, H - WATERMARK_H / 2);
  ctx.textAlign = "left";

  return c.toDataURL("image/png");
}

// Rounded-rectangle path helper (no built-in on some older canvases).
function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function loadImg(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

// Word-wrap helper: returns an array of lines that all fit inside maxWidth.
function wrapLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}
